<!-- =========================================================================================
  File Name: DashboardAnalytics.vue
  Description: Dashboard Analytics
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div class="relative mb-20 md:mb-0 ">
    <Pagesubtitle subTitle="Easily connect over 2000 platforms to Feedback Link. Send automatic requests to 100 customers each month for free (Zapier plan required for selected apps)."/>
    <div v-if="disabled" class="text-center">
      <div class="inline-block w-100 my-20">
        <img class="mx-auto w-32" src="@/assets/images/misc/automate.png"/>
        <p class="my-8 font-medium text-lg" > {{ disabledMessage }}</p>
        <vs-button v-if="$store.getters['dash/subscription'].isTrial" class="w-full" @click="chatToUs">Talk to us</vs-button>
        <vs-button v-else class="w-full" @click="$router.push('/dash/settings/plan')">See Plans</vs-button>
      </div>
    </div>
    <div v-else>
      <div class="vx-row">
        <!-- CARD 1: CONGRATS -->
        <div class="vx-col w-full lg:w-3/4 mb-base flex ">
            <vx-card class="text-center" title="What is Zapier?">
              <template slot="actions">
                    <small class="flex cursor-pointer">
                      <img src="@/assets/images/logo/zapier.png" class="h-8"/>
                    </small>
              </template>
              <div class="text-dark text-left">
                <p>Zapier is an automation tool that connects over 2000 popular apps (SalesForce, Facebook, Shopify etc.) to each other. With Zapier you can set <span class="font-medium">actions</span> to be completed when specific <span class="font-medium">trigger</span> events occur <span class="font-medium">accross different apps</span> your business currently use.</p>
                <vs-collapse type="margin" class="small my-4" >
                  <vs-collapse-item icon-pack="feather" icon-arrow="icon-help-circle">
                    <div slot="header">
                      Examples
                    </div>
                    <div class="">
                      <p class="font-medium mb-4">Some examples of what you can do with Zapier:</p>
                      <p class="my-4 italic">Connect your Twitter account to send you a private message on Facebook Messenger (<span class="font-medium">action</span>) everytime you receive a Tweet (<span class="font-medium">trigger</span>);</p>
                      <p class="my-4 italic">Connect your Gmail account to upload email attachments to Dropbox (<span class="font-medium">action</span>) everytime a new email arrives (<span class="font-medium">trigger</span>).</p>
                      <p class="font-medium mb-4">Feedback Link examples:</p>
                      <p class="my-4 italic">Connect your Shopify store to send a review/feedback request (<span class="font-medium">action</span>) everytime a new order is placed (<span class="font-medium">trigger</span>).</p>
                      <p class="my-4 italic">Connect your Customer Support Suite to send a review/feedback request (<span class="font-medium">action</span>) everytime a support ticket is resolved (<span class="font-medium">trigger</span>).</p>
                    </div>
                  </vs-collapse-item>
                </vs-collapse>
                <p class="mt-4">Zapier acts as the secure “middle-man” platform where you’ll go to <span class="font-medium">connect apps</span> and create <span class="font-medium">actions</span> and <span class="font-medium">triggers</span> between them.</p>
              </div>
            </vx-card>
        </div>
        <div class="vx-col w-full lg:w-1/4 mb-base">
            <vx-card class="p-5 text-center bg-transparent" @click="() => {$router.push('/dash/getfeedback/sendinvites/text')}">
              <a href="https://zapier.com/apps" target="_blank">Supported Zapier apps ></a>
            </vx-card>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full lg:w-3/4 mb-base flex">
            <vx-card class="text-center" title="Step by step">
              <div class="text-dark text-left">
                <p>In Zapier, you’ll want to specify a trigger from an application your business uses, and set up a “Send Email Request” or “Send Text/SMS Request” action with Feedback Link. This will automate feedback/review requests each time that trigger event occurs. </p>
                <p class="my-4">Note that the trigger will need to provide at least the customer’s email or mobile number to the Feedback Link action in order for a request to be sent.</p>
              </div>
              <div class="text-dark text-left my-8">
                <h4>1. Copy your special Feedback Link key</h4>
                <div class="overflow-auto my-4">
                  <vs-input class="float-left w-3/4" readonly :value="$store.getters['dash/zapierKey']"/>
                  <vs-button class="float-left w-1/4" @click="copyKey" type="border" icon-pack="feather" :icon="copyText === 'Copied!' ? 'icon-check':''">{{ copyText }}</vs-button>
                </div>
                <p>This is called your “API Key” and is like a password - keep it private and secure.</p>
              </div>
              <div class="text-dark text-left my-8">
                <h4>2. Activate Feedback Link in Zapier</h4>
                <p class="my-4">After clicking the below button you’ll need to log in to your Zapier account or create a new one (free).</p>
                <p class="my-4">Then click “Accept Invite & Build Zap” (A trigger and an action together is called a “Zap”).</p>
                <vs-button class="min-w-md" type="border" href="https://zapier.com/developer/public-invite/121623/42eb269633d52481bc87b8c651a82fc3/" target="_blank">Activate Feedback Link in Zapier</vs-button>
              </div>
              <div class="text-dark text-left my-8">
                <h4>3. Create a Zap and select a trigger</h4>
                <p class="my-4">In Zapier, select which trigger event will activate a request to your customer.</p>
                <vs-collapse type="margin" class="small my-4" >
                  <vs-collapse-item icon-pack="feather" icon-arrow="icon-help-circle">
                    <div slot="header">
                      Detailed steps
                    </div>
                    <div class="text-lg">
                      <p class="mb-4">After clicking “Accept Invite & Build…”, you’ll be taken to your “Zaps” dashboard. Click “Create Zap”.</p>
                      <p class="my-4">In your new Zap, you’ll need to identify which <span class="font-medium">trigger</span> will cause a request to be sent to your customer (<span class="font-medium">action</span>). This trigger should be naturally associated with a customer email or mobile number (e.g. a new order in your eCommerce platform or a new sale from your POS software). </p>
                      <p class="my-4">Sign in to your app to give access to Zapier to receive the trigger from your app. Select the default account for your app after signing in.</p>
                      <p class="my-4">Zapier will ask you to test the trigger. Run a test trigger & click continue.</p>
                    </div>
                  </vs-collapse-item>
                </vs-collapse>
              </div>
              <div class="text-dark text-left my-8">
                <h4>4. Specify which action needs to be completed for your trigger</h4>
                <p class="my-4">In Zapier, indicate that it needs to run a Feedback Link action when your trigger occurs.</p>
                <vs-collapse type="margin" class="small my-4" >
                  <vs-collapse-item icon-pack="feather" icon-arrow="icon-help-circle">
                    <div slot="header">
                      Detailed steps
                    </div>
                    <div class="text-lg">
                      <p class="mb-4">In the “Choose app & event” search box, type Feedback Link and select “Feedback Link (1.0.0)”. Choose whether you’d like to send Text or Email requests to customers.</p>
                      <p class="my-4">Sign in to Feedback Link by pasting the key you copied in step 1. When prompted, choose the default account called “Feedback Link [Your Business Name]” in the “Choose account” step.</p>
                      <p class="my-4">Under “Set up action” you’ll need to tell Zapier which information coming from your selected trigger will be provided to the Feedback Link action. For instance, if your trigger is “New Order” in your eCommerce app, you’ll need to tell Zapier which piece of order information to use as the “Customer Email Address” field. Naturally it will be named something like “Email”, “Customer Email” or “Order Email Address”. </p>
                      <p class="my-4">Don’t manually type anything into these boxes - be sure to only select from the provided drop-down choices for each field.</p>
                      <p class="my-4">Select “Test & Continue”.</p>
                      <p class="my-4">Finally, click “Activate Zap” to turn on the automation.</p>
                    </div>
                  </vs-collapse-item>
                </vs-collapse>
                <p class="my-4 font-medium">All set! Your customers should now automatically receive requests for feedback/reviews each time your trigger occurs!</p>
                <p class="my-4">Getting stuck? <a href="" @click.prevent="chatToUs">Talk to us.</a></p>
              </div>
            </vx-card>
        </div>
      </div>
      <div>
        <h5 class="block">Feedback Link Zapier Actions</h5>
        <p class="my-4">Currently you can run two Feedback Link actions from triggers in your apps through Zapier:</p>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full lg:w-3/4 mb-base">
          <div class="vx-row">
            <div class="vx-col w-full lg:w-1/2 mb-base flex">
              <vx-card class="text-left" >
                <div class="pr-4">
                  <h4>
                    <img src="@/assets/images/misc/zapierthingy.png" class="float-left h-6 my-3 mr-4"/>Send Feedback/Review Request to Customer via Email
                  </h4>
                </div>
                <p class="my-4 text-lg">This action can take 5 possible inputs:</p>
                <div class="pt_table">
                  <div>
                    <span class="text-dark font-medium">*Required</span>
                    <p class="hdg">Customer email address</p>
                  </div>
                  <div class="bg-white">
                    <span>Optional</span>
                    <p class="hdg">Customer first name</p>
                </div>
                <div>
                    <span>Optional</span>
                    <p class="hdg">Delay</p>
                    <p class="desc">The number of days to delay the request with (if needed).</p>
                </div>
                <div class="bg-white">
                  <span>Optional</span>
                  <p class="hdg">Order number</p>
                  <p class="desc">If you’re connecting your eCommerce platform - you can provide the order number associated with this feedback request. This is shown in your Feedback Link dashboard so you can relate a piece of feedback to an order.</p>
              </div>
              <div>
                  <span>Optional</span>
                  <p class="hdg">Reference</p>
                  <p class="desc">(Optional) Shown in your Feedback Link dashboard, so you can relate a piece of feedback to a reference. If you're setting this up for eCommerce, use the "Order Number" field instead.</p>
              </div>
                </div>
              </vx-card>
            </div>
            <div class="vx-col w-full lg:w-1/2 mb-base flex">
              <vx-card class="text-left" >
                <div class="pr-4">
                  <h4>
                    <img src="@/assets/images/misc/zapierthingy.png" class="float-left h-6 my-3 mr-4"/>Send Feedback/Review Request to Customer via Text/SMS
                  </h4>
                </div>
                <p class="my-4 text-lg">This action can take 3 possible inputs:</p>
                <div class="pt_table">
                  <div>
                    <span class="text-dark font-medium">*Required</span>
                    <p class="hdg">Customer mobile number</p>
                  </div>
                  <div class="bg-white">
                    <span>Optional</span>
                    <p class="hdg">Customer first name</p>
                </div>
                <div class="">
                    <span>Optional</span>
                    <p class="hdg">Delay</p>
                    <p class="desc">The number of days to delay the request with (if needed).</p>
                </div>
                </div>
              </vx-card>
            </div>
          </div>
        </div>
      </div>
  </div>
</div>
</template>


<script>
import Pagesubtitle from './components/ptcomponents/Pagesubtitle.vue'

function copyTextToClipboard(text) {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  navigator.clipboard.writeText(text).then(function() {}, function(err) {});
}

export default {
  data () {
    return {
      setupData: false,
      copyText: 'Copy'
    }
  },
  methods: {
    copyKey () {
      copyTextToClipboard(this.$store.getters['dash/zapierKey'])
      this.copyText = 'Copied!'
    }
  },
  computed: {
    disabled () {
      let allowAutomationOverride = typeof this.$store.state.dash.viewData.business.allowAutomation !== 'undefined' ? this.$store.state.dash.viewData.business.allowAutomation : false
      if (allowAutomationOverride) return false
      if (this.$store.getters['dash/subscription'].isTrial) return true
      return this.$store.getters['dash/subscription'].plan === '1m' || this.$store.getters['dash/subscription'].plan === '1y'
    },
    disabledMessage () {
      if (!this.disabled) return false
      if (this.$store.getters['dash/subscription'].isTrial) {
        return 'Due to risk of abuse, we\'ve disabled automation during the free trial. Please reach out if you\'d like to enable it.'
      } else {
        return 'Automation isn\'t available on your current plan. If you require this feature, you\'ll need to upgrade to the "Engage" plan or higher.'
      }
    }
  },
  components: {
    Pagesubtitle
  },
  created () {
  }
}
</script>

<style lang="scss">
/*! rtl:begin:ignore */
#dashboard-analytics {
  .greet-user{
    position: relative;

    .decore-left{
      position: absolute;
      left:0;
      top: 0;
    }
    .decore-right{
      position: absolute;
      right:0;
      top: 0;
    }
  }

  @media(max-width: 576px) {
    .decore-left, .decore-right{
      width: 140px;
    }
  }
}
/*! rtl:end:ignore */
</style>
